import {of as observableOf, Observable} from 'rxjs';

import {takeWhile, map, first, catchError} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostListener,
  Input,
  NgZone,
} from '@angular/core';
import {Component, ViewChild} from '@angular/core';
import {AbstractControlComponent} from './abstract-control';
import {ViewTabComponent} from '../viewtab.component';
import {CacheService} from '../../../cache/cache.service';
import {LanguageService} from '../../../language/language.service';
import {ValuesObject} from '../detail-tabconfig';
import {DisplayAttribute} from '../../displayattribute';
import {CtrlFileDownloadComponent} from './ctrl-filedownload.component';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {ErrorService} from '../../../error/error.service';
import {FileDropDirective} from '../../../shared/component/id-file-drop/file-drop';

@Component({
  selector: 'ctrl-fileupload',
  template: `
    @if (isImage | async) {
      <p class="image">
        @if (formControl.value != null) {
          <img
            src="data:image/jpg;base64,{{ formControl.value }}"
            [alt]="alternateText"
            class="rounded"
          />
        }
        @if (formControl.value == null) {
          <span>
            @if (defaultImageUrl != null) {
              <img [src]="defaultImageUrl" [alt]="alternateText" class="rounded" />
            }
            @if (defaultImageUrl == null) {
              <i class="material-icons">image</i>
            }
            <span class="empty">{{ getNoContentText() }}</span>
          </span>
        }
      </p>
    }
    @if ((isImage | async) != true) {
      <p class="other">
        @if (formControl.value == null) {
          <span class="empty">{{ getNoContentText() }}</span>
        }
        @if (objectUrl | async) {
          <a [href]="objectUrl | async" [download]="downloadFileName" target="_blank">{{
            contentLinkText
          }}</a>
        }
      </p>
    }
    @if (getCaughtObservable(isEnabled) | async) {
      <div>
        <div class="input-area">
          <div
            fileDrop
            class="file-drop"
            [class.file-is-over]="fileIsOver"
            (fileOver)="fileOver($event)"
            (onFileDrop)="onFileDrop($event)"
            (click)="fileInput.click()"
          >
            <svg
              class="drop-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="50"
              height="43"
              viewBox="0 0 50 43"
            >
              <path
                d="M48.4 26.5c-.9 0-1.7.7-1.7 1.7v11.6h-43.3v-11.6c0-.9-.7-1.7-1.7-1.7s-1.7.7-1.7 1.7v13.2c0 .9.7 1.7 1.7 1.7h46.7c.9 0 1.7-.7 1.7-1.7v-13.2c0-1-.7-1.7-1.7-1.7zm-24.5 6.1c.3.3.8.5 1.2.5.4 0 .9-.2 1.2-.5l10-11.6c.7-.7.7-1.7 0-2.4s-1.7-.7-2.4 0l-7.1 8.3v-25.3c0-.9-.7-1.7-1.7-1.7s-1.7.7-1.7 1.7v25.3l-7.1-8.3c-.7-.7-1.7-.7-2.4 0s-.7 1.7 0 2.4l10 11.6z"
              ></path>
            </svg>
            <span>{{ getDropLabel() }}</span>
          </div>
          <div class="buttons">
            <button mat-raised-button color="primary" (click)="fileInput.click()">
              <language key="CtrlFileUploadBrowse" defaultValue="Browse…"></language>
            </button>
            <button mat-button color="warn" (click)="clear()" class="btn-clear">
              <language key="CtrlFileUploadClear" defaultValue="Clear"></language>
            </button>
          </div>
          <input
            type="file"
            (change)="onFileInput($event)"
            #fileInput
            hidden
            [accept]="fileTypes"
          />
        </div>
        @if (formControl.touched && formControl.hasError('required')) {
          <mat-error>
            <language key="ControlErrorRequired" defaultValue="This field is required."></language>
          </mat-error>
        }
        @if (formControl.touched && fileTooLarge) {
          <mat-error>
            <language
              key="CtrlFileUploadFileTooLarge"
              defaultValue="The uploaded file exceeded the maximum file size of {0} MB."
              [args]="[maxFileSize]"
            ></language>
          </mat-error>
        }
        @if (formControl.touched && invalidFileType) {
          <mat-error>
            <language
              key="CtrlFileUploadInvalidFileType"
              defaultValue="You must choose a file that matches one of these types: {0}"
              [args]="[displayFileTypes]"
            ></language>
          </mat-error>
        }
      </div>
    }
  `,
  styleUrls: ['./ctrl-fileupload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlFileuploadComponent extends AbstractControlComponent {
  static dataUrlRegex: RegExp = /^data:[^;]+;base64,([A-Za-z0-9+\/=]+)$/;

  public fileIsOver: boolean = false;

  public fileOver(fileIsOver: boolean): void {
    this.fileIsOver = fileIsOver;
  }

  private targetBinaryType: boolean = true;
  invalidFileType: boolean = false;
  @Input()
  public objectType: string = null;

  @ViewChild(FileDropDirective) fileDropDirective;

  private objectBlob: Observable<Blob> = observableOf(null);
  private objectUrl: Observable<SafeUrl> = observableOf(null);
  fileName: string = null;
  fileTypes: string = null;
  dropLabel: string = 'Drop file here';

  contentLinkText: string = 'Click here to view the value of this attribute';
  _downloadFileName: string = null;
  private defaultDownloadFileName: string = null;

  get downloadFileName(): string {
    if (this.fileName != null) return this.fileName;
    if (this._downloadFileName != null) return this._downloadFileName;
    return this.defaultDownloadFileName;
  }

  noContentText: string = null;
  defaultNoContentText: string = 'No data.';

  getNoContentText(): string {
    return this.noContentText ? this.noContentText : this.defaultNoContentText;
  }

  maxFileSize: number = 10; // in megabytes
  fileTooLarge: boolean = false;

  // image specific
  alternateText: string = '';
  defaultImageUrl: string = null;

  constructor(
    parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    private cacheService: CacheService,
    private sanitizer: DomSanitizer,
    errorService: ErrorService,
  ) {
    super(parent, 'Value', 'Value', changeDetector, _ngZone, errorService);
    this.cacheService
      .getLanguageResource()
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
      )
      .subscribe(lr => {
        this.dropLabel = LanguageService.translate('CtrlFileUploadDropLabel', 'Drop file here', lr);
        this.contentLinkText = LanguageService.translate(
          'CtrlFileUploadContentLinkText',
          'Click here to view the value of this attribute',
          lr,
        );
        this.defaultNoContentText = LanguageService.translate(
          'CtrlFileUploadDefaultNoContentText',
          'No data.',
          lr,
        );
        this.defaultDownloadFileName = LanguageService.translate('DefaultFileName', 'download', lr);
      });
  }

  protected initValidators(): void {
    super.initValidators();
    if (this.sourceVoObs != null) {
      this.sourceVoObs.subscribe((vo: ValuesObject<any>) => {
        if (vo == null || vo.Values == null) {
          this.objectBlob = observableOf(null);
          this.objectUrl = observableOf(null);
        } else if (vo.BindingSource === 'object' || vo.BindingSource === 'localization') {
          this.objectBlob = this.cacheService
            .getDisplayAttribute(vo.BindingPath, this.objectType)
            .pipe(
              first(),
              map((da: DisplayAttribute) => {
                let blob: Blob = null;
                if (da.DataType === 'Binary') {
                  blob = CtrlFileDownloadComponent.b64ToBlob(vo.Values);
                } else {
                  let mimeType: string = 'text/plain';
                  if (typeof vo.Values === 'string' && vo.Values.startsWith('<?xml')) {
                    mimeType = 'application/xml';
                  }
                  blob = CtrlFileDownloadComponent.textToBlob(vo.Values, mimeType);
                }
                return blob;
              }),
              catchError((error, caught) => this.handleError(error, caught)),
            );

          this.objectUrl = this.objectBlob.pipe(
            map((blob: Blob) => {
              if (blob == null) return null;
              return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
            }),
            catchError((error, caught) => this.handleError(error, caught)),
          );
        } else {
          // TODO: handle non object bindings (not in display attribute)
          console.error('Can not handle non object/localization bindings for UocFileUpload', vo);
        }
      });
    }

    if (this._config.propertyExists('AllowedFileTypes')) {
      this._config
        .getPropertyValues('AllowedFileTypes')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((allowed: string) => {
          // Format is: *.gif; *.jpg; *.jpeg; *.png; *.bmp; *.tif; *.tiff
          // needed: .gif,.jpg,.jpeg,.png,.bmp,.tif,.tiff
          if (allowed == null || allowed.trim().length === 0) {
            this.fileTypes = null;
          } else {
            this.fileTypes = allowed.replace(/[* ]/g, '').replace(/;/g, ',');
          }
        });
    }
    if (this._config.propertyExists('AlternateText')) {
      this._config
        .getPropertyValues('AlternateText')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((alternateText: string) => {
          if (alternateText == null) {
            this.alternateText = '';
          } else {
            this.alternateText = alternateText;
          }
        });
    }

    if (this._config.propertyExists('PromptForNoValue')) {
      this._config
        .getPropertyValues('PromptForNoValue')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((text: string) => {
          this.noContentText = text;
        });
    }

    if (this._config.propertyExists('MaxFileSize')) {
      this._config
        .getPropertyValues('MaxFileSize')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((maxFS: any) => {
          this.maxFileSize = Number(maxFS);
          if (this.maxFileSize == null || isNaN(this.maxFileSize)) {
            this.maxFileSize = 1;
          }
        });
    }

    if (this._config.propertyExists('ImageUrl')) {
      this._config
        .getPropertyValues('ImageUrl')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((url: string) => {
          if (url == null || url.startsWith('~/')) {
            this.defaultImageUrl = null;
          } else if (url.startsWith('http://') || url.startsWith('https://')) {
            this.defaultImageUrl = url;
          } else {
            this.defaultImageUrl = 'layoutAssets/' + url;
          }
        });
    }

    if (this._config.propertyExists('PromptedFileName')) {
      this._config
        .getPropertyValues('PromptedFileName')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((fileName: any) => {
          if (fileName != null) {
            this._downloadFileName = String(fileName);
          }
        });
    }
  }

  protected initTarget(): void {
    super.initTarget();

    this.target.pipe(first()).subscribe(t => {
      if (t.source === 'object' || t.source === 'localization') {
        this.cacheService
          .getDisplayAttribute(t.path, this.objectType)
          .pipe(
            catchError((error, caught) => this.handleError(error, caught)),
            first(),
          )
          .subscribe((da: DisplayAttribute) => {
            this.targetBinaryType = da.DataType === 'Binary';
          });
      }
    });
  }

  getDropLabel(): string {
    return this.fileName ? this.fileName : this.dropLabel;
  }

  protected formatSource(source: any): any {
    return source;
  }

  protected formatTarget(value: any): any {
    return value;
  }

  get isImage(): Observable<boolean> {
    return this.getCaughtObservable(
      this._config.getPropertyValues('ContentType').pipe(
        map(valObj => {
          if (valObj != null) {
            return valObj === 'Image';
          }
          return false;
        }),
      ),
    );
  }

  public setDefaultValue(): void {
    this.fileName = null;
    this.invalidFileType = false;
    this.fileTooLarge = false;
    super.setDefaultValue();
  }

  clear(): void {
    this.formControl.markAsTouched();
    this.fileName = null;
    this.invalidFileType = false;
    this.fileTooLarge = false;
    this.formControl.setValue(null);
    this.changeDetector.markForCheck();
  }

  public onFileDrop(file: File): void {
    if (file == null) return;
    this.fileTooLarge = false;
    this.invalidFileType = false;
    this.formControl.markAsTouched();
    if (
      this.fileTypes != null &&
      file.name != null &&
      this.fileTypes.split(',').indexOf('.' + file.name.split('.').pop()) === -1
    ) {
      // invalid file type
      this.invalidFileType = true;
      this.changeDetector.markForCheck();
      return;
    }
    if (
      file.size != null &&
      this.maxFileSize != null &&
      file.size > this.maxFileSize * 1024 * 1024
    ) {
      this.fileTooLarge = true;
      this.changeDetector.markForCheck();
      return;
    }

    const reader = new FileReader();
    if (this.targetBinaryType) {
      reader.readAsDataURL(file);
      reader.onload = () => {
        let result: string;
        if (typeof reader.result === 'string') {
          result = <string>reader.result;
        } else {
          result = String.fromCharCode.apply(null, new Uint16Array(<ArrayBuffer>reader.result));
        }
        const matches = CtrlFileuploadComponent.dataUrlRegex.exec(result);
        if (matches != null) {
          this.fileName = file.name;
          this.formControl.setValue(matches[1]);
          this.changeDetector.markForCheck();
        }
      };
    } else {
      reader.readAsText(file);
      reader.onload = () => {
        this.fileName = file.name;
        this.formControl.setValue(reader.result);
        this.changeDetector.markForCheck();
      };
    }
  }

  public onFileInput(event: any): void {
    if (
      event == null ||
      event.target == null ||
      event.target.files == null ||
      event.target.files.length === 0
    )
      return;
    this.onFileDrop(event.target.files[0]);
  }

  get displayFileTypes(): string {
    return this.fileTypes.replace(/,/g, ', ');
  }

  @HostListener('dragover', ['$event'])
  public onDragOver(event: any): void {
    if (this.fileDropDirective != null) {
      this.fileDropDirective.onDragOver(event);
    }
  }

  @HostListener('dragleave', ['$event'])
  public onDragLeave(event: any): void {
    if (this.fileDropDirective != null) {
      this.fileDropDirective.onDragLeave(event);
    }
  }

  @HostListener('drop', ['$event'])
  public onDrop(event: any): void {
    if (this.fileDropDirective != null) {
      this.fileDropDirective.onDrop(event);
    }
  }
}
